/**
 * HubTile — a single Olive hub tile (X.5.1; restyled X.6.3 to the
 * design-refresh "Warm" prototype).
 *
 * Left-aligned card: a chip top-left, title + subhead below, a soft lift on
 * hover, and a directional arrow that fades in top-right (↗ external / →
 * internal). White (paper) surface, no border, x.6.1 shadow/radius/motion
 * tokens — zero raw palette hexes. The chip is one of three treatments:
 *   - dove:   the Peace dove brand mark; dark-sage chip + cream dove by
 *             default, or the heather (lavender) wash + heather-deep dove
 *             when the tile asks for `chip: 'heather'` (Prospects)
 *   - pepper: cream chip + the full-color SPICE pepper
 *   - image:  faint brand-tinted chip + the real vendor logo PNG (D1;
 *             operator-delivered, supersedes the prototype's monogram)
 *
 * External tiles render as `<a target="_blank">`, internal tiles as a router
 * `<Link>`. `onActivate` (X.6.3) lets Home record the open for "Jump back in".
 */

import { type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { HubBrandMark } from './HubBrandMark';
import { FULL_BLEED_LOGOS, SPICE_PEPPER_SVG, VENDOR_CHIP_TINT } from '@/lib/hubLogos';
import { resolveTile, type HubTile as HubTileDef, type HubTileIcon } from '@/lib/hubTiles';

/** Reusable chip (also used by Home's "Jump back in" recent cards). */
export function HubChip({ icon, size = 50 }: { icon: HubTileIcon; size?: number }): ReactNode {
  const base = 'flex shrink-0 items-center justify-center rounded-card overflow-hidden';
  const dims = { width: size, height: size };
  if (icon.kind === 'dove') {
    // Founder walk (2026-07-06, second pass): the Home hub dove chip is
    // DARK SAGE (sage-600), not forest. Tonight's icon-spec change flipped
    // this chip to forest (sage-800); founder ruled "sage is nice but doesn't
    // pop — is there a darker sage?" — so the dove chip takes the ramp's
    // darker-sage step, which pops against the cream card while still reading
    // sage rather than forest. Home-hub-only carve-out: the AiToolCard icon
    // tiles STAY forest per the same walk (she liked those). See the DL5 note
    // in src/index.css.
    //
    // `chip: 'heather'` (Prospects, founder-directed 2026-07-25): the SAME
    // dove brand mark on the heather (warm-lavender) wash with a
    // heather-deep dove — the sanctioned pairing from the clinical identity
    // set (heather-deep on heather 6.98:1, AA at chip size; src/index.css).
    // A pale chip on a paper card also takes the vendor-chip hairline ring
    // for edge definition.
    const heather = icon.chip === 'heather';
    const chipClass = heather
      ? 'bg-heather ring-1 ring-inset ring-sage-800/[6%]'
      : 'bg-sage-600';
    return (
      <span className={`${base} ${chipClass} shadow-sh1 [&_svg]:w-auto`} style={dims}>
        <HubBrandMark
          className="flex [&_svg]:h-auto"
          style={{
            color: heather ? 'var(--color-heather-deep)' : 'var(--color-cream)',
            width: Math.round(size * 0.6),
          }}
          label=""
        />
      </span>
    );
  }
  if (icon.kind === 'pepper') {
    // R2 (operator): the SPICE pepper SVG viewBox is TALL (130 x 220, ratio
    // ~0.59). The old `[&_svg]:h-auto` left width unconstrained, so the tall
    // pepper stretched to the square chip and skewed. Constrain by HEIGHT
    // (~72% of the chip) with width auto, so it scales proportionally and
    // stays upright + centered. The CSS var carries the size-derived px height
    // into the injected SVG (it has no inline style we can set directly).
    return (
      <span
        className={`${base} bg-cream shadow-sh1 [&_svg]:h-[var(--pepper-h)] [&_svg]:w-auto`}
        style={{ ...dims, '--pepper-h': `${Math.round(size * 0.72)}px` } as CSSProperties}
        role="img"
        aria-label="SPICE"
        // SPICE_PEPPER_SVG is a trusted static brand asset (src/lib/hubLogos.ts).
        dangerouslySetInnerHTML={{ __html: SPICE_PEPPER_SVG }}
      />
    );
  }
  // Real vendor logo on a chip washed with a faint tint of that brand's color
  // (D1; falls back to paper if the logo has no tint mapping), with a hairline
  // inset ring. The logo fills more of the box than before (0.78 vs 0.6) so it
  // reads less padded. Decorative (alt=""): the tile title already names the
  // tool. `26` is the alpha byte (~15%) appended to the 6-digit brand hex.
  // An app-icon logo is its own chip: it fills the box, with no tint, ring
  // or margin around it.
  if (FULL_BLEED_LOGOS.has(icon.src)) {
    return (
      <span className={`${base} shadow-sh1`} style={dims} data-full-bleed="">
        <img src={icon.src} alt="" className="h-full w-full object-cover" />
      </span>
    );
  }
  const tint = VENDOR_CHIP_TINT[icon.src];
  return (
    <span
      className={`${base} shadow-sh1 ring-1 ring-inset ring-sage-800/[6%] ${tint ? '' : 'bg-paper'}`}
      style={tint ? { ...dims, backgroundColor: `${tint}26` } : dims}
    >
      <img
        src={icon.src}
        alt=""
        className="object-contain"
        style={{ width: size * 0.78, height: size * 0.78 }}
      />
    </span>
  );
}

const TILE_CLASS =
  'group relative block rounded-card border border-transparent bg-paper px-4 pt-4 pb-[15px] text-inherit no-underline shadow-sh2 transition-[box-shadow,transform] duration-[var(--dur-slow)] ease-quiet hover:-translate-y-[3px] hover:shadow-sh3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function HubTile({
  tile,
  slug,
  onActivate,
}: {
  tile: HubTileDef;
  slug: string;
  onActivate?: () => void;
}): ReactNode {
  const resolved = resolveTile(tile, slug);
  const Arrow = resolved.isExternal ? ArrowUpRight : ArrowRight;
  const body = (
    <>
      <Arrow
        size={16}
        aria-hidden="true"
        className="absolute right-4 top-4 text-sage-300 opacity-0 transition-all duration-[var(--dur-base)] ease-quiet group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-sage-400 group-hover:opacity-100 motion-reduce:transition-none"
      />
      <HubChip icon={resolved.icon} />
      <div className="mt-3.5">
        <div className="text-[0.90625rem] font-semibold leading-tight tracking-[-0.01em] text-sage-800">
          {resolved.label}
        </div>
        <div className="mt-1 pr-3.5 text-[0.78125rem] leading-snug text-ink-soft">
          {resolved.subheading}
        </div>
      </div>
    </>
  );

  if (resolved.isExternal) {
    return (
      <a
        data-testid={`hub-tile-${resolved.id}`}
        href={resolved.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onActivate}
        className={TILE_CLASS}
      >
        {body}
      </a>
    );
  }
  return (
    <Link
      data-testid={`hub-tile-${resolved.id}`}
      to={resolved.href}
      onClick={onActivate}
      className={TILE_CLASS}
    >
      {body}
    </Link>
  );
}
